// The Oryx 16-Bit Fantasy world sheet as *sets* (the table lookup the view-tile rulesets draw
// from). Every number is a `world_NNNN` frame index in `public/assets/oryx/world.json`; the
// per-frame labels live in `public/assets/oryx/labels.json` (tools/oryx-labels.py), and the
// piece semantics below were measured from the sheet (alpha footprint per 8-px region for the
// ground and liquid sets, luminance per 4-px strip for the wall kit) — see docs/15-view-tiling.md.
//
// Sheet facts the offsets rely on: the sheet is 57 cells wide, so "one row down" is +57.
// Credit: Oryx Design Lab — www.oryxdesignlab.com.

import { N, E, S, W, NE, SE, SW, NW } from './grid';

export const ROW = 57;
export const frame = (i: number): string => `world_${String(i).padStart(4, '0')}`;

// ---- Dungeon kit rows: [floor ×3][dark floor ×4][stairs ×2][wall ×18] --------------------------
// Light bevelled flagstone (plain, cracked, cracked 2), dark floor (plain, framed, cracked, grate),
// stairs (up-right, down-left), then the 18 wall pieces.
export interface DungeonKitRow { base: number; wallBase: number; stairs: boolean }
export const DUNGEON_ROWS: Record<string, DungeonKitRow> = {
  grey:       { base: 59,   wallBase: 68,   stairs: true },
  darkGrey:   { base: 116,  wallBase: 125,  stairs: true },
  olive:      { base: 173,  wallBase: 182,  stairs: true },
  brick:      { base: 230,  wallBase: 239,  stairs: true },
  mossy:      { base: 287,  wallBase: 296,  stairs: true },
  green:      { base: 344,  wallBase: 353,  stairs: true },
  teal:       { base: 401,  wallBase: 410,  stairs: true },
  blueGrey:   { base: 458,  wallBase: 467,  stairs: true },
  yellow:     { base: 515,  wallBase: 524,  stairs: true },
  ruled:      { base: 572,  wallBase: 581,  stairs: true },
  darkTeal:   { base: 629,  wallBase: 638,  stairs: true },
  brownGrey:  { base: 686,  wallBase: 695,  stairs: true },
  ivy:        { base: 743,  wallBase: 752,  stairs: false },   // 750/751 are two extra pieces before the standard 18
  cliff:      { base: 800,  wallBase: 809,  stairs: false },   // orange rock: 807/808 are diagonal slopes, then the standard 18
  leafy:      { base: 857,  wallBase: 866,  stairs: true },    // hedge walls with leafy edges
  snow:       { base: 914,  wallBase: 923,  stairs: true },
  snowCliff:  { base: 1028, wallBase: 1037, stairs: true },
  whiteCliff: { base: 1085, wallBase: 1094, stairs: true },
  tan:        { base: 1142, wallBase: 1151, stairs: true },
};

/**
 * Wall piece offset (from `wallBase`) by the 4-bit mask of *connected* walls (N=1 E=2 S=4 W=8).
 * Measured: the pieces are front-facing tops; a side without a connected wall shows its bevel,
 * the south side without a wall shows the dark face, and a concave corner shows a notch.
 */
export const WALL16: Readonly<Record<number, number>> = {
  0: 0,            // isolated pillar
  [E]: 1,          // west end
  [W]: 3,          // east end
  [E | W]: 2,      // straight, face south
  [S]: 4,          // top end of a vertical run
  [N | S]: 5,      // vertical run
  [N]: 6,          // bottom end of a vertical run
  [E | S]: 7,      // corner: continues east and south
  [W | S]: 8,      // corner: continues west and south
  [N | E]: 9,      // corner: continues north and east
  [N | W]: 10,     // corner: continues north and west
  [N | E | S | W]: 11,
  [E | W | S]: 12, // T, no north
  [N | W | S]: 13, // T, no east
  [N | E | S]: 14, // T, no west
  [N | E | W]: 15, // T, no south
};
/** Cracked variants: offset 16 is a cracked vertical run (mask N|S), 17 a cracked straight (E|W). */
export const WALL_CRACKED: Readonly<Record<number, number>> = { [N | S]: 16, [E | W]: 17 };

// ---- Ground sets (grass etc.): fills, light edges/corners, outward lips, inward pieces ----
// Measured on the atlas (transparent pixels per 4-px edge strip; /tmp-style `atlas-edges.py` in
// LESSONS.md). Layout from base g:
//   g+0, g+1       the two full fills (plain, flecked)
//   g+2..g+5       LIGHT inward edges: ~1 px eroded on N, E, S, W (9–13 transparent px)
//   g+6..g+9       LIGHT inward outer corners: two eroded sides NE, NW, SW, SE (30–39 px)
//   g+10..g+24     outward lips, drawn on the NON-ground neighbour (grass tongues ~3 px deep)
//   g+57..g+67     HEAVY inward island / caps / bars / outer corners (2–3 px eroded, ~100 px)
//   g+68           a fill with a 1-px nick in each corner (4 px) — not a "four-bite centre"
//   g+69..g+72     inward straight edges, light weight (24–40 px): nothing N, E, W, S
//   g+73, g+74     second bar variants (E–W, N–S)
// There are NO inner-corner "bite" pieces in the set; the first cut used g+6..g+9 as bites and
// g+68 as a bitten centre, and every cell diagonal to a patch grew two full eroded edges.
//
// The set is two-sided and the two sides are alternatives, not layers: either the ground cell
// erodes (inward pieces) and the neighbour stays plain, or the ground cell is a full fill and the
// neighbour carries the lip. Drawing both prints a base-coloured line between the grass and its
// own tongue — the "grid lines" of the 22 Oct reviews. Kits pick one (`ground.edges`).
export interface GroundSet { base: number; fillWeights?: readonly number[]; lightEdges?: boolean }
export const GROUND: Record<string, GroundSet> = {
  grass:     { base: 942 },
  sand:      { base: 1056 },
  tan:       { base: 1170 },
  orange:    { base: 1284 },
  olive:     { base: 1398 },
  olive2:    { base: 1512 },   // yellower olive (a different colour, not an alternate pattern)
  snow:      { base: 1626 },
  peach:     { base: 1740, lightEdges: false },
  peach2:    { base: 1854, lightEdges: false },
  darkGreen: { base: 1968 },
};
export const GROUND_FILLS = [0, 1] as const;
export const GROUND_FILL_WEIGHTS = [10, 4] as const;
/** Light alternates of the inward straight edges (keyed by the inward piece offset). */
export const GROUND_EDGE_LIGHT: Readonly<Record<number, number>> = { 69: 2, 70: 3, 72: 4, 71: 5 };
/** Light alternates of the inward outer corners (keyed by the inward piece offset). */
export const GROUND_CORNER_LIGHT: Readonly<Record<number, number>> = { 64: 7, 65: 6, 66: 8, 67: 9 };
/** How often an inward edge/corner uses its light alternate (per mille). */
export const GROUND_EDGE_LIGHT_SHARE = 350;
/** Outward lips (drawn on a *neighbouring* non-ground cell), by the 4-bit mask of ground neighbours. */
export const GROUND_LIP: Readonly<Record<number, number>> = {
  [N | W]: 10, [N | E]: 11, [S | E]: 12, [S | W]: 13,
  [N | E | W]: 14, [N | E | S]: 15, [S | E | W]: 16, [N | W | S]: 17,
  [N | E | S | W]: 18,
  [N]: 19, [E]: 20, [S]: 21, [W]: 22,
};
/** Inward pieces (drawn on the ground cell itself), by the 4-bit mask of same-ground neighbours. */
export const GROUND_IN: Readonly<Record<number, number | readonly number[]>> = {
  0: 57,                 // island
  [E]: 58,               // cap, ground continues east
  [E | W]: [59, 73],     // east–west bar (two variants)
  [W]: 60,               // cap, continues west
  [S]: 61,               // cap, continues south
  [N | S]: [62, 74],     // north–south bar (two variants)
  [N]: 63,               // cap, continues north
  [E | S]: 64,           // outer corner NW
  [S | W]: 65,           // outer corner NE
  [N | E]: 66,           // outer corner SW
  [N | W]: 67,           // outer corner SE
  [E | S | W]: 69,       // edge: nothing north
  [N | S | W]: 70,       // edge: nothing east
  [N | E | S]: 71,       // edge: nothing west
  [N | E | W]: 72,       // edge: nothing south
};

// ---- Liquid sets: fill (two frames, animate) + additive shoreline overlays --------------------
// From base L (the TL corner piece): same row +1 T, +2 TR; next row L, single, R; then BL, B, BR.
export interface LiquidSet { base: number }
export const LIQUID: Record<string, LiquidSet> = { water: { base: 1602 }, acid: { base: 1605 }, lava: { base: 1608 }, mud: { base: 1611 } };
/** Shoreline piece offset by the 4-bit mask of *land* neighbours (cardinals that are not this liquid). */
export const SHORE: Readonly<Record<number, number>> = {
  [N | W]: 0, [N]: 1, [N | E]: 2,
  [W]: ROW, [N | E | S | W]: ROW + 1, [E]: ROW + 2,
  [S | W]: 2 * ROW, [S]: 2 * ROW + 1, [S | E]: 2 * ROW + 2,
  [N | E | W]: 3 * ROW + 1,   // cap open to the south
  [N | E | S]: 3 * ROW + 2,   // cap open to the west
  [E | S | W]: 4 * ROW + 1,   // cap open to the north
  [N | S | W]: 4 * ROW + 2,   // cap open to the east
  [N | S]: 7 * ROW + 2,       // east–west channel (land north and south)
  [E | W]: 8 * ROW + 2,       // north–south channel (land west and east)
};
export const LIQUID_FILL = [3 * ROW, 4 * ROW] as const;        // ripple frames A / B
export const LIQUID_STONE = [5 * ROW + 2, 6 * ROW + 2] as const; // stepping stone in the liquid
/** Lone inner-corner nubs (diagonal is land, both adjacent cardinals liquid). */
export const SHORE_NUB: Readonly<Record<number, number>> = { [NW]: 7 * ROW, [NE]: 7 * ROW + 1, [SW]: 8 * ROW, [SE]: 8 * ROW + 1 };
/** Dedicated combinations: [land mask, nub mask] → offset. */
export const SHORE_COMBO: ReadonlyArray<readonly [number, number, number]> = [
  [N | W, SE, 5 * ROW], [N | E, SW, 5 * ROW + 1], [S | W, NE, 6 * ROW], [S | E, NW, 6 * ROW + 1],
  [0, SW | SE, 9 * ROW], [0, NW | NE, 9 * ROW + 1], [0, NW | NE | SW | SE, 9 * ROW + 2],
  [0, NW | SW, 10 * ROW], [0, NE | SE, 10 * ROW + 1],
];

// ---- Planks, fences, fills, props ------------------------------------------------------------
export const PLANKS = { ns: [1390, 1391, 1392], ew: [1447, 1448, 1449], dark: [1504, 1505, 1506], broken: 1393, framed: 1394, braced: [1395, 1396] } as const;
export const FENCE_WOOD = { ew: [980, 981, 982, 983], ns: [984, 985], post: 986 } as const;
export const FILLS = {
  green: 771, tan: 772, olive: 773, darkOlive: 774, tallGrass: 775, orangeDirt: 776, snow: 777, sand: 778, dirt: 779, lavaA: 780, lavaB: 781, grassMud: [782, 783, 784],
  // 828-837 by measured mean colour: moss green, dark green, olive, grey rock, green, orange rock, brown, dark green 2, void dither (19 % opaque), brown rock
  mossTex: 828, darkGreenTex: 829, oliveTex: 830, greyRock: 831, greenTex: 832, orangeRock: 833, brownTex: 834, darkGreenTex2: 835, voidDither: 836, brownRock: 837,
  mudPuddles: [838, 839, 840, 841],
} as const;
export const SHADOW = { topThin: 2139, topMid: 2140, topThick: 2141, topHalf: 2142, bottomThin: 2143, bottomMid: 2144, bottomThick: 2145, bottomHalf: 2146, dropFlat: [2147, 2148, 2149], dropRound: [2150, 2151, 2152], lineBottom: 2153, ditherBottom: 2154 } as const;
export const PROPS = {
  cobweb: { nw: 144, ne: 145, sw: 146, se: 147, full: 148, strand: 149 },
  blood: [150, 151, 152, 153], slime: [154, 155], torch: [156, 157], fire: [97, 98], brazier: [99, 100],
  bones: [90, 91, 92, 93], bonePiles: [94, 95], skullPile: 96, rubble: 89, tombstone: 87, tombstoneBroken: 88,
  flowers: [159, 160, 161], paleRubble: 162, boulder: 164, starPlant: [165, 166], puddle: [167, 168], bloodPool: [169, 170],
  rocksWhite: [618, 619, 620], rocksOrange: [621, 622, 623], rocksBrown: [1199, 1200, 1201, 1202, 1203, 1204], rocksDarkGrey: [1256, 1257, 1258, 1259, 1260, 1261], rocksRed: [1313, 1314, 1315, 1316, 1317, 1318],
  bushDense: [102, 103], bushSmall: [104, 105, 106], bushDenseOrange: [107, 108], bushSmallOrange: [109, 110, 111], lilyPad: [112, 113],
  doors: { wood: 201, archOpen: 202, knocker: 203, wood2: 204, plate: 205, ajar: 206, plain: 207, ironClosed: 208, ironOpen: 209, iceClosed: 210, iceOpen: 211, boarded: [212, 213], portal: 214, portcullis: 258, gateOpen: 259, blue: 260 },
  crystals: { orange: 217, grey: [218, 219] }, rocks: 220, cacti: [221, 222, 223], roundBush: [224, 225], roundBushBrown: [226, 227],
  trees: { oak: 273, fruit: 274, autumn: 275, dead: 276, stump: 277, pine: 278, snowPine: 279, oakSingle: [444, 445, 446], autumnSingle: [447, 448, 449], bramble: [450, 451, 452], pineSingle: [453, 454, 455], snowPineSingle: [615, 616, 617], snowPineSingle2: [624, 625, 626] },
  leaves: [280, 281, 282, 283], shrubs: { green: [501, 502, 503], orange: [504, 505, 506], tufts: [507, 508, 509], teal: [510, 511, 512] },
  hedges: { green: [558, 559, 560], orange: [561, 562, 563] }, tallGrassOrange: [564, 565, 566],
  chest: { closed: 261, gold: 262, trap: 263, empty: 264 }, barrel: [268, 269], bucket: [270, 271], crate: [435, 436],
  urns: { grey: [380, 381, 382, 383, 384], greyBroken: 385, blue: [437, 438], blueBroken: 439, green: [440, 441], greenBroken: 442, red: [494, 495], redBroken: 496, orange: [497, 498], orangeBroken: 499 },
  traps: { spikes: 429, plate: 430, rune: 431, skullPlate: 432, wheel: 433, pit: 434 },
  pedestals: { fountain: 486, bowl: 487, gem: 488, serpent: 489, orb: 490, ring: 491, altar: 492, figure: 493 },
  statues: { hooded: 376, lying: 377, archer: 378, knight: 379 }, cauldron: [374, 375],
  furniture: { cabinet: [315, 316], bookshelf: 317, shelf: 318, desk: [319, 320], stool: 321, throne: 322, tableCandles: [323, 324], cupboard: [327, 328] },
  well: 548, stoneRing: 547, goldPile: 549, greyPile: 550, skull: 555, hornedSkull: 556,
  orbs: [657, 658, 659, 660, 661, 662], bannerRed: [1374, 1431, 1488], bannerBlue: [1375, 1432, 1489], heraldry: [1545, 1546, 1547, 1548, 1549, 1550],
  pipes: { grey: [1614, 1615, 1616, 1617, 1618, 1619, 1671, 1672, 1673, 1674, 1675, 1676, 1728, 1729, 1730, 1731, 1732, 1733] },
  pools: { water3: [[1620, 1621, 1622], [1677, 1678, 1679], [1734, 1735, 1736]], water2: [[1623, 1624], [1680, 1681]], water1: 1737 },
} as const;
